import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { download } from 'src/app/helpers/download/download';
import { InterventionInstanceReminderLogsInterface } from 'src/app/models/interface/intervention-instance-reminder-logs.interface';
import { InterventionInstanceReminderInterface } from 'src/app/models/interface/intervention-instance-reminder.interface';
import { environment } from '../../../../environments/environment';
import { PayloadInterface } from '../../../models/interface/payload.interface';
import { HelperService } from '../../helper/helper.service';

@Injectable({
  providedIn: 'root',
})
export class ReminderService {
  public header: HttpHeaders = new HttpHeaders();
  private backendUrl: string = environment.backendURL;

  constructor(private http: HttpClient, private helperService: HelperService) {
    this.header = this.header.set('Content-Type', 'application/json');
    this.header = this.helperService.setLocaleFromStorage(this.header);
  }

  /**
   * AAS2 API eCoach - Set intervention instance reminder
   * This function creates an intervention instance reminder.
   *
   * @params number instanceId - ID of the intervention instance.
   *         number receiverId - ID of the receipient.
   *         number maxTimes - Number of times the user should be reminded.
   *         number start - Timestamp when the reminder begins.
   *         number repeatTime - Time interval of the repetition.
   *         string repeatUnit - Time interval unit of the repetition.
   *         string contentGreeting - Content of the reminder greeting.
   *         string contentText - Content of the reminder text.
   *         string contentClosing - Content of the reminder closing.
   *         string contentSignature - Individual sender signature
   * @return Observable<any> - An observable for any response.
   */
  public setInterventionInstanceReminder(
    instanceId: number,
    receiverId: number,
    maxTimes: number,
    start: number,
    repeatTime: number,
    repeatUnit: 'minute' | 'hour' | 'day',
    contentGreeting?: string,
    contentText?: string,
    contentClosing?: string,
    contentSignature?: string,
  ) {
    const payload: PayloadInterface = {
      data: {
        type: 'reminders',
        attributes: {
          receiver_id: receiverId,
          configuration: {
            max_times: maxTimes,
            start,
            pause: repeatTime,
            time_unit: repeatUnit
          },
          content: {
            greeting: contentGreeting,
            text: contentText,
            closing: contentClosing,
            signature: contentSignature,
          },
        },
      },
    };
    const options = { headers: this.header, observe: 'response' } as const;
    const url = `${this.backendUrl}/api/v1/ecoach/reminders/interventions/instances/${instanceId}`;
    return this.http.post(url, payload, options);
  }

  /**
   * AAS2 API eCoach - Update intervention instance reminder
   * This function updates an intervention instance reminder.
   *
   * @params number reminderId - ID of the reminder.
   *         number receiverId - ID of the receipient.
   *         number maxTimes - Number of times the user should be reminded.
   *         number start - Timestamp when the reminder begins.
   *         number repeatTime - Time interval of the repetition.
   *         string repeatUnit - Time interval unit of the repetition.
   *         string contentGreeting - Individual greeting text (e.g. "Hello,")
   *         string contentText - Content of the reminder text.
   *         string contentClosing - Individual closing text (e.g. "Best wishes,")
   *         string contentSignature - Individual sender text (e.g. "your eSano Team")
   * @return Observable<any> - An observable for any response.
   */
  public updateInterventionInstanceReminder(
    reminderId: number,
    receiverId: number,
    maxTimes: number,
    start: number,
    repeatTime: number,
    repeatUnit: 'minute' | 'hour' | 'day',
    contentGreeting: string,
    contentText: string,
    contentClosing: string,
    contentSignature: string,
    locale: string,
  ): Observable<any> {
    const payload: PayloadInterface = {
      data: {
        type: 'reminders',
        attributes: {
          receiver_id: receiverId,
          configuration: {
            start: start,
            max_times: maxTimes,
            pause: repeatTime,
            time_unit: repeatUnit
          },
          content: {
            greeting: contentGreeting,
            text: contentText,
            closing: contentClosing,
            signature: contentSignature,
            locale: locale,
          },
        },
      },
    };
    return this.http.patch<any>(
      `${this.backendUrl}/api/v1/ecoach/reminders/${reminderId}`,
      payload,
      { headers: this.header, observe: 'response' },
    );
  }

  /**
   * AAS2 API eCoach - Get an Intervention Instance Reminder
   * This function returns an intervention instance reminder.
   *
   * @params number reminderId - ID of the reminder.
   * @return Observable<any> - An observable for any response.
   */
  public getInterventionInstanceReminder(reminderId: number): Observable<any> {
    const options = { headers: this.header, observe: 'response', } as const;
    const url = `${this.backendUrl}/api/v1/ecoach/reminders/${reminderId}`;
    return this.http.get<any>(url, options).pipe(
      map((result) => {
        const reminder: InterventionInstanceReminderInterface = result.body?.data;
        this.convertReminder(reminder);
        return result;
      }),
    );
  }

  /**
   * AAS2 API eCoach - Get all Intervention Instance Reminders of eCoach
   * This function returns all intervention instance reminders of the eCoach.
   *
   * @return Observable<any> - An observable for any response.
   */
  public getAllInterventionInstanceReminders(): Observable<any> {
    const options = { headers: this.header, observe: 'response' } as const;
    const url = `${this.backendUrl}/api/v1/ecoach/reminders?limit=0`;
    return this.http.get<any>(url, options).pipe(
      map((result) => {
        const reminders: InterventionInstanceReminderInterface[] = result.body?.data;
        this.convertReminders(reminders);
        return result;
      }),
    );
  }

  /**
   * AAS2 API eCoach - Delete an Intervention Instance Reminder
   * This function deletes an intervention instance reminder.
   *
   * @params number reminderId - ID of the reminder.
   * @return Observable<any> - An observable for any response.
   */
  public deleteInterventionInstanceReminder(reminderId: number): Observable<any> {
    return this.http.delete<any>(`${this.backendUrl}/api/v1/ecoach/reminders/${reminderId}`, {
      headers: this.header,
      observe: 'response',
    });
  }

  /**
   * AAS2 API eCoach - Get all Reminder logs of Study
   * This function returns all reminder logs of all intervention instance reminders of the study.
   *
   * @params number studyId - ID of the study.
   * @params number interventionInstanceId - ID of the Intervention instance.
   * @params number userId - ID of the receiver.
   * @return Observable<InterventionInstanceReminderLogsInterface[]>
   */
  public getReminderLogsOfStudy(
    studyId: number,
    interventionInstanceId?: number | string,
    userId?: number,
  ): Observable<InterventionInstanceReminderLogsInterface[]> {
    let params = new HttpParams().set('limit', 0);
    if (interventionInstanceId != undefined) {
      // Array would be valid, but is not currently used
      params = params.set('interventionInstanceIDs', interventionInstanceId);
    } else if (userId != undefined) { // Only get logs of user as fallback in case instanceId is missing
      params = params.set('userID', userId);
    }
    const options = { headers: this.header, observe: 'response', params, } as const;
    const url = `${this.backendUrl}/api/v1/ecoach/studies/${studyId}/reminders/logs`;
    return this.http.get(url, options).pipe(
      map((result) => {
        const reminderLogs: InterventionInstanceReminderLogsInterface[] = result.body?.['data'];
        this.convertReminders(reminderLogs);
        return reminderLogs;
      }),
    );
  }

  /**
   * Converts old content-attribute 'ending' of reminders to new 'signature' content-attribute
   */
  private convertReminders(
    reminders: InterventionInstanceReminderLogsInterface[] | InterventionInstanceReminderInterface[] | null | undefined,
  ) {
    for (const r in reminders) {
      this.convertReminder(reminders[r]);
    }
  }

  /**
   * Converts old content-attribute 'ending' of reminder to new 'signature' content-attribute
   */
  private convertReminder(reminder: InterventionInstanceReminderLogsInterface | InterventionInstanceReminderInterface) {
    const content = reminder.attributes.content;
    if ((content as any).ending !== undefined) {
      if (!content.signature && (content as any).ending) {
        content.signature = (content as any).ending;
      }
      delete (content as any).ending;
    }
  }

  /**
   * AAS2 API eCoach - Export all Reminder logs of Study as CSV
   * This function returns all reminder logs of all intervention instance reminders of the study in csv.
   *
   * @params number studyId - ID of the study.
   * @return Observable<any> - An observable for any response.
   */
  public getReminderLogsOfStudyCSV(studyId: number, userId?: number, separator?: ';', deletedReminders = 1): Observable<any> {
    this.header = this.header.set('Content-Type', '*/*');
    this.header = this.header.set('Accept', '*/*');
    let params = new HttpParams();
    if (userId !== undefined) {
      params = params.set('userID', userId);
    }
    if (separator !== undefined) {
      params = params.set('separator', separator);
    }
    if (separator !== undefined) {
      params = params.set('separator', separator);
    }
    if (deletedReminders !== undefined) {
      params = params.set('deletedReminders', deletedReminders);
    }
    let downloaded = false;
    return this.http
      .get<any>(`${this.backendUrl}/api/v1/ecoach/studies/${studyId}/reminders/logs/csv/report`, {
        headers: this.header,
        reportProgress: true,
        observe: 'events',
        params: params,
        responseType: 'blob' as 'json',
      })
      .pipe(
        download((jsonResponse) => {
          const filename = `reminderLogs-studyId${studyId}-${new Date().toISOString()}.csv`;
          const binaryData = [];
          binaryData.push(jsonResponse);
          const downloadLink = document.createElement('a');
          downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: 'application/csv' }));
          downloadLink.setAttribute('download', filename);
          document.body.appendChild(downloadLink);
          if (!downloaded) {
            downloadLink.click();
            URL.revokeObjectURL(downloadLink.href);
            downloaded = true;
          }
        }),
      );
  }
}
